import React, { useState } from 'react';
import { Tooltip } from '@jobtarget/ui-library';
import { JobData } from '../../api/JobsService';
import { AdvertisingStatus } from '../../data/enums/jobEnums';
import JobTooltip from './JobToolTip';
import { normalizeAdvertisingStatus } from '../../utils/jobTransformations';
import Spinner from '../Spinner';
import { EASY_APPLY_TOOLTIP_MESSAGE } from '../../data/constants/constants';

interface JobsTableProps {
  jobs: JobData[];
  onAttach?: (jobId: number) => void;
  getQuestionnaireName?: (jobId: number) => string;
  onManage?: (jobId: number) => void;
  jobsWithQuestionnaires: Set<number>;
  getInterviewCount?: (jobId: number) => number;
  isLoadingInterviews?: boolean;
  isLoadingQuestionnaires?: boolean;
  loadingJobIds?: Set<number>; // Track which specific jobs are loading
  isMobile: boolean;
  onInterviewCountClick?: (jobId: number) => void;
  relationshipManagerEmail?: string | null;
}

const JobsTable: React.FC<JobsTableProps> = ({
  jobs = [],
  onAttach,
  getQuestionnaireName,
  onManage,
  jobsWithQuestionnaires,
  getInterviewCount,
  isLoadingInterviews,
  isLoadingQuestionnaires,
  loadingJobIds = new Set(),
  isMobile,
  onInterviewCountClick,
  relationshipManagerEmail
}) => {
  const [hoveredJob, setHoveredJob] = useState<JobData | null>(null);
  
  const getAdvertisingStatusBadge = (status: AdvertisingStatus) => {
    // Map status to Bootstrap classes
    const getStatusClasses = () => {
      switch(status) {
        case AdvertisingStatus.UNPOSTED:
          return 'bg-light-info text-info';
        case AdvertisingStatus.ACTIVE:
          return 'bg-light-success text-dark-success';
        case AdvertisingStatus.EXPIRED:
          return 'bg-dark-fill';
        default:
          return 'bg-light text-dark';
      }
    };
      
    return (
      <span 
        className={`${getStatusClasses()} px-2 py-1 rounded d-inline-block`}
        style={{
          fontWeight: 500,
          fontSize: '0.8rem'
        }}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="table-responsive card mt-2" style={{ 
      overflowX: isMobile ? 'auto' : 'visible',
      overflowY: 'visible'
    }}>
      <table className="table mb-0">
        <thead>
          <tr className="bg-light">
            <th style={{ textAlign: 'left', paddingLeft: '1.5rem', fontWeight: 400, fontSize: '0.875rem' }}>Name</th>
            {/* <th style={{ textAlign: 'left', fontWeight: 400, fontSize: '0.875rem', whiteSpace: 'nowrap', }}>Advertising Status</th> */}
            <th style={{ textAlign: 'left', fontWeight: 400, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Completed Screeners</th>
            <th style={{ textAlign: 'left', fontWeight: 400, fontSize: '0.875rem' }}>Questionnaire</th>
            <th style={{ textAlign: 'left', fontWeight: 400, fontSize: '0.875rem' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr key={job.id}>
              <td style={{ paddingLeft: '1rem' }}>
                <div className="d-flex align-items-start">
                  <div className="me-3 mt-1 position-relative" onMouseEnter={() => setHoveredJob(job)} onMouseLeave={() => setHoveredJob(null)}>
                    <svg 
                      width="1.2rem"  
                      height="1.2rem" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ 
                          color: '#1bc5bd',
                      }}
                    >
                      <g clipPath="url(#clip0_2584_11412)">
                          <path d="M12 1.875C6.375 1.875 1.875 6.42188 1.875 12C1.875 17.625 6.375 22.125 12 22.125C17.5781 22.125 22.125 17.625 22.125 12C22.125 6.42188 17.5312 1.875 12 1.875ZM12 0.375C18.375 0.375 23.625 5.625 23.625 12C23.625 18.4219 18.375 23.625 12 23.625C5.57812 23.625 0.375 18.4219 0.375 12C0.375 5.625 5.57812 0.375 12 0.375ZM10.3125 16.5H10.875V10.875H10.3125C9.98438 10.875 9.75 10.6406 9.75 10.3125V9.9375C9.75 9.65625 9.98438 9.375 10.3125 9.375H12.5625C12.8438 9.375 13.125 9.65625 13.125 9.9375V16.5H13.6875C13.9688 16.5 14.25 16.7812 14.25 17.0625V17.4375C14.25 17.7656 13.9688 18 13.6875 18H10.3125C9.98438 18 9.75 17.7656 9.75 17.4375V17.0625C9.75 16.7812 9.98438 16.5 10.3125 16.5ZM12 5.25C12.7969 5.25 13.5 5.95312 13.5 6.75C13.5 7.59375 12.7969 8.25 12 8.25C11.1562 8.25 10.5 7.59375 10.5 6.75C10.5 5.95312 11.1562 5.25 12 5.25Z" fill="currentColor"/>
                      </g>
                      <defs>
                          <clipPath id="clip0_2584_11412">
                          <rect width="24" height="24" fill="white"/>
                          </clipPath>
                      </defs>
                    </svg>
                    <JobTooltip 
                      job={job}
                      position="right"
                      show={hoveredJob?.id === job.id}
                      isMobile={isMobile}
                    />
                  </div>
                  <div className="d-flex flex-column">
                    <span style={{ fontWeight: 600, fontSize: '0.938rem', marginLeft: '0.5rem' }}>
                      {job.title}
                    </span>
                    <small className="text-muted" style={{ fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                      {job.locations?.[0]?.city}, {job.locations?.[0]?.region}
                    </small>
                  </div>
                </div>
              </td>
              {/* <td>
                {getAdvertisingStatusBadge(
                  job.advertisingStatus 
                    ? (job.advertisingStatus as AdvertisingStatus)
                    : normalizeAdvertisingStatus(job.jobOrigin as AdvertisingStatus)
                )}
              </td> */}
              <td style={{ textAlign: 'left' }}>
                {(() => {
                  const count = getInterviewCount ? getInterviewCount(job.id) : 0;
                  
                  if (count > 0) {
                    return (
                      <span 
                        onClick={() => {
                          console.log('🔗 Clicked interview count for job:', job.id, job.title);
                          onInterviewCountClick?.(job.id);
                        }}
                        style={{ 
                          cursor: 'pointer', 
                          color: '#007bff', 
                          textDecoration: 'underline',
                          fontWeight: 500
                        }}
                      >
                        {count}
                      </span>
                    );
                  } else {
                    return <span>–</span>;
                  }
                })()}
              </td>
              <td style={{ textAlign: 'left' }}>
                {getQuestionnaireName ? getQuestionnaireName(job.id) : '–'}
              </td>
              <td>
                {loadingJobIds.has(job.id) ? (
                  <button 
                    type="button" 
                    className="btn btn-outline-primary"
                    disabled
                  >
                    <Spinner size="sm" color="primary" />
                  </button>
                ) : job.easyApplyEnabled === false ? (
                  <Tooltip
                    message={(
                      relationshipManagerEmail
                        ? <>{EASY_APPLY_TOOLTIP_MESSAGE} at <a href={`mailto:${relationshipManagerEmail}`}>{relationshipManagerEmail}</a></>
                        : <>{EASY_APPLY_TOOLTIP_MESSAGE}.</>
                    ) as unknown as string}
                    position="top"
                    maxWidth={260}
                    textAlign="center"
                  >
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled
                      style={{ cursor: 'not-allowed' }}
                    >
                      {jobsWithQuestionnaires.has(job.id) ? 'Manage' : 'Attach'}
                    </button>
                  </Tooltip>
                ) : jobsWithQuestionnaires.has(job.id) ? (
                  <button 
                    type="button" 
                    className="btn btn-light-primary text-primary"
                    onClick={() => onManage?.(job.id)}
                  >
                    Manage
                  </button>
                ) : (
                  <button 
                    type="button" 
                    className="btn btn-outline-primary"
                    onClick={() => onAttach?.(job.id)}
                  >
                    Attach
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default JobsTable;